import useRequest from "@/lib/request/useRequest";
import { object, number, string, InferType } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { useMemo, useCallback } from "react";
import {
    ApplicationMethod,
    NitrogenSimulationType,
    Status,
} from "@/types/enums";
import { sortArrayByKey } from "@/lib/utils/sorting";
import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
} from "@heroui/react";
import SelectInput from "../inputs/SelectInput";
import { SimulationType } from "@/types/nitrogenPlan";
import { getSimulationTypeDisplayName } from "@/types/nitrogenPlan";
import { InsightsTableRow } from "@/lib/utils/insightsCropZones";
import { CropConfiguration, CropStage } from "@/types/cropConfiguration";
import { getTodayWithTimeTravel } from "@/lib/utils/date";
import { getSimulationTypeColor } from "@/lib/utils/nitrogenPlan";

const scema = object().shape({
    rateModifier: string().optional().oneOf(["=", "+", "-"]),
    editRate: number().min(0).optional(),
    fertilizerProduct: number().optional(),
    applicationMethod: string()
        .optional()
        .oneOf(Object.values(ApplicationMethod)),
    simulationModel: string().optional().oneOf(Object.values(SimulationType)),
    targetGrowthStage: number().optional(),
});

type FormType = InferType<typeof scema>;

function BulkEditNitrogenPlan({
    fieldsWithPlan,
    onFinish,
}: {
    fieldsWithPlan: InsightsTableRow[];
    onFinish: () => void;
}) {
    const { useMutateRequest, useGetRequest } = useRequest();

    const form = useForm<FormType>({
        resolver: yupResolver(scema),
        defaultValues: {
            rateModifier: "=",
            editRate: undefined,
            fertilizerProduct: undefined,
            simulationModel: undefined,
            targetGrowthStage: undefined,
        },
    });

    const hasSameCrop = useMemo(() => {
        if (fieldsWithPlan.length === 0) return false;
        return fieldsWithPlan.every(
            (field) =>
                field.active_planting_operation.crop_configuration.id ===
                fieldsWithPlan[0].active_planting_operation.crop_configuration
                    .id,
        );
    }, [fieldsWithPlan]);

    const cropConfiguration = useGetRequest<CropConfiguration>({
        url: `crop_configurations/${fieldsWithPlan[0].active_planting_operation.crop_configuration.id}/`,
        enabled: hasSameCrop && fieldsWithPlan.length > 0,
    });

    const singleFieldId = useMemo(() => {
        if (fieldsWithPlan.length === 0 || fieldsWithPlan.length > 1)
            return undefined;
        return fieldsWithPlan[0].id;
    }, [fieldsWithPlan]);

    const singleCropZoneId =
        fieldsWithPlan.length === 1
            ? (fieldsWithPlan[0].crop_zone_id ?? undefined)
            : undefined;

    const minimumAdvisorCropStageQuery = useGetRequest<CropStage>({
        url: `fields/${singleFieldId}/minimum_advisor_crop_stage/`,
        enabled: !!singleFieldId,
        params: {
            target_date: getTodayWithTimeTravel().format("YYYY-MM-DD"),
            crop_zone_id: singleCropZoneId,
        },
    });
    const targetGrowthStageOptions = useMemo(() => {
        return (
            cropConfiguration.data?.stages
                ?.filter((stage) => {
                    if (
                        !singleFieldId ||
                        !minimumAdvisorCropStageQuery.data?.days_after_planting
                    )
                        return true;

                    return (
                        stage.days_after_planting >
                        minimumAdvisorCropStageQuery.data?.days_after_planting
                    );
                })
                .map((stage) => ({
                    id: stage.id,
                    description: stage.growth_stage,
                })) ?? []
        );
    }, [
        cropConfiguration.data?.stages,
        minimumAdvisorCropStageQuery.data?.days_after_planting,
        singleFieldId,
    ]);

    // const getFertilizers = useGetRequest<FertilizerProduct[]>({
    //     url: "/fertilizers/",
    //         enabled: action === "set-product",
    // });

    // const fertilizerOptions = useMemo(() => {
    //     const dryFertilizers =
    //         getFertilizers.data
    //             ?.filter(
    //                 (fertilizer) =>
    //                     fertilizer.fertilizer_type === FertilizerType.Dry,
    //             )
    //             .sort((a, b) => a.name.localeCompare(b.name))
    //             .map((fertilizer) => ({
    //                 value: fertilizer.id,
    //                 label: fertilizer.name,
    //             })) ?? [];
    //     const liquidFertilizers =
    //         getFertilizers.data
    //             ?.filter(
    //                 (fertilizer) =>
    //                     fertilizer.fertilizer_type === FertilizerType.Liquid,
    //             )
    //             .sort((a, b) => a.name.localeCompare(b.name))
    //             .map((fertilizer) => ({
    //                 value: fertilizer.id,
    //                 label: fertilizer.name,
    //             })) ?? [];
    //     return [
    //         {
    //             value: FertilizerType.Dry,
    //             label: t("Dry Fertilizers"),
    //             options: dryFertilizers,
    //         },
    //         {
    //             value: FertilizerType.Liquid,
    //             label: t("Liquid Fertilizers"),
    //             options: liquidFertilizers,
    //         },
    //     ];
    // }, [getFertilizers.data, t]);

    // const methodOptions = useMemo(() => {
    //     const types = [
    //         {
    //             id: ApplicationMethod.InjectedKnifedCoultered,
    //             description: "Injected/Knifed/Coultered",
    //         },
    //         { id: ApplicationMethod.Dribble, description: "Dribble" },
    //         { id: ApplicationMethod.Broadcast, description: "Broadcast" },
    //         { id: ApplicationMethod.Topdress, description: "Topdress" },
    //         { id: ApplicationMethod.YDrop, description: "Y-Drop" },
    //         {
    //             id: ApplicationMethod.BroadcastTillage,
    //             description: "Broadcast Tillage",
    //         },
    //         { id: ApplicationMethod.TwoXTwo, description: "2x2" },
    //         { id: ApplicationMethod.Furrow, description: "Furrow" },
    //         { id: ApplicationMethod.Fertigation, description: "Fertigation" },
    //     ];
    //     return sortArrayByKey(types, "description");
    // }, []);

    const simulationModelOptions = useMemo(() => {
        const types = Object.values(NitrogenSimulationType)
            .filter(
                (type) =>
                    type !== NitrogenSimulationType.No_Rate &&
                    type !== NitrogenSimulationType.Max_Rate,
            )
            .map((type) => ({
                id: type,
                description: getSimulationTypeDisplayName(
                    type as unknown as SimulationType,
                ),
                startContent: (
                    <div
                        className="rounded-md w-6 h-6 flex items-center justify-center "
                        style={{
                            backgroundColor: getSimulationTypeColor(type),
                        }}
                    ></div>
                ),
            }));
        return sortArrayByKey(types, "description");
    }, []);

    const mutateFieldDefaults = useMutateRequest({
        method: "patch",
        url: "/fields/bulk_update/",
        successMessage: "Fields updated successfully.",
        errorMessage: "Failed to update fields.",
    });

    const mutateCropZoneTargets = useMutateRequest({
        method: "patch",
        url: "/crop_zones/bulk_update/",
        errorMessage: "Failed to update crop zones.",
    });

    const handleSubmit = useCallback(
        async (data: FormType) => {
            const fieldUpdates = new Map<
                number,
                {
                    id: number;
                    default_nitrogen_plan_type?: string;
                    active_target_growth_stage?: number;
                }
            >();
            const cropZoneUpdates = new Map<
                number,
                { id: number; active_target_growth_stage?: number }
            >();
            fieldsWithPlan.forEach((row) => {
                const isZoneRow = row.crop_zone_id != null;
                const existing = fieldUpdates.get(row.id);
                fieldUpdates.set(row.id, {
                    id: row.id,
                    default_nitrogen_plan_type:
                        data.simulationModel ?? undefined,
                    active_target_growth_stage: isZoneRow
                        ? existing?.active_target_growth_stage
                        : data.targetGrowthStage,
                });
                const zoneId = isZoneRow
                    ? row.crop_zone_id
                    : row.crop_zones?.find(
                          (zone) =>
                              zone.status == null ||
                              zone.status === Status.Active,
                      )?.id;
                if (zoneId != null) {
                    cropZoneUpdates.set(zoneId, {
                        id: zoneId,
                        active_target_growth_stage: data.targetGrowthStage,
                    });
                }
            });

            await Promise.all([
                mutateFieldDefaults.mutateAsync({
                    data: [...fieldUpdates.values()],
                }),
                cropZoneUpdates.size > 0
                    ? mutateCropZoneTargets.mutateAsync({
                          data: [...cropZoneUpdates.values()],
                      })
                    : Promise.resolve(),
            ]);
            onFinish();
        },
        [fieldsWithPlan, mutateFieldDefaults, mutateCropZoneTargets, onFinish],
    );

    return (
        <Modal isOpen onClose={onFinish}>
            <ModalContent>
                <FormProvider {...form}>
                    <form>
                        <ModalHeader>Edit Rate/Scenario</ModalHeader>
                        <ModalBody>
                            {/* <>
                                <label className="mb-2 block text-sm font-medium text-gray-700">
                                    Edit Nitrogen Rate
                                </label>
                                <div className="flex flex-row gap-4">
                                    <SelectInput
                                        name="rateModifier"
                                        options={[
                                            {
                                                id: "=",
                                                description: "",
                                                startContent: (
                                                    <FontAwesomeIcon
                                                        icon={faEquals}
                                                        className="text-primary-500"
                                                    />
                                                ),
                                            },
                                            {
                                                id: "+",
                                                description: "",
                                                startContent: (
                                                    <FontAwesomeIcon
                                                        icon={faPlus}
                                                        className="text-success-500"
                                                    />
                                                ),
                                            },
                                            {
                                                id: "-",
                                                description: "",
                                                startContent: (
                                                    <FontAwesomeIcon
                                                        icon={faMinus}
                                                        className="text-danger-500"
                                                    />
                                                ),
                                            },
                                        ]}
                                        isRequired
                                        className="max-w-[80px]"
                                    />
                                    <NumberInput
                                        name="editRate"
                                        endContent={
                                            <span className="text-sm">
                                                {formatUnit("lbs_ac", "kg_ha")}
                                            </span>
                                        }
                                        isRequired
                                    />
                                </div>
                            </> */}
                            {/* <SectionedAutoCompleteInput
                                name="fertilizerProduct"
                                label="Fertilizer Product"
                                labelPlacement="outside"
                                groups={fertilizerOptions}
                                placeholder="Select Fertilizer Product"
                                required
                            /> */}
                            {/* {action === "set-method" && (
                                <SelectInput
                                    name="applicationMethod"
                                    label="Application Method"
                                    labelPlacement="outside"
                                    options={methodOptions}
                                    placeholder="Select Application Method"
                                    isRequired
                                />
                            )} */}
                            <SelectInput
                                name="simulationModel"
                                label="Simulation Model"
                                labelPlacement="outside"
                                options={simulationModelOptions}
                                placeholder="Select Model"
                                isRequired
                            />
                            <SelectInput
                                name="targetGrowthStage"
                                label="Target Growth Stage"
                                labelPlacement="outside"
                                options={targetGrowthStageOptions}
                                placeholder="Select Growth Stage"
                                isDisabled={!hasSameCrop}
                                isLoading={cropConfiguration.isLoading}
                                isRequired
                            />
                        </ModalBody>
                        <ModalFooter className="flex flex-col gap-1 w-full">
                            {!hasSameCrop && (
                                <div className="text-sm text-warning-500">
                                    Fields have different crops, please select
                                    fields with the same crop.
                                </div>
                            )}
                            <div className="self-end">
                                <Button
                                    color="primary"
                                    onPress={() => {
                                        form.trigger();
                                        form.handleSubmit(handleSubmit)();
                                    }}
                                    isLoading={mutateFieldDefaults.isPending}
                                    isDisabled={
                                        mutateFieldDefaults.isPending ||
                                        !hasSameCrop
                                    }
                                    fullWidth
                                >
                                    Update Rate Recommendations
                                </Button>
                            </div>
                        </ModalFooter>
                    </form>
                </FormProvider>
            </ModalContent>
        </Modal>
    );
}

export default BulkEditNitrogenPlan;
